import { useConfirmation } from './ConfirmDialog';
import React, { useState } from 'react';
import { Asset, Location } from '../types';
import { locationsService } from '../services/locations';
import CreateLocationInline from './CreateLocationInline';

interface Props {
  facilityId: string;
  locations: Location[];
  legacyAssets: Asset[];
  onChanged: () => void | Promise<void>;
}

/** Manage the structured tree without hiding roots or repeating descendants. */
const FacilityLocations: React.FC<Props> = ({ facilityId, locations, legacyAssets, onChanged }) => {
  const { confirm, dialog } = useConfirmation();
  const [editing, setEditing] = useState<string>();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const unconverted = legacyAssets.filter(asset => !locations.some(location => location.id === asset.id));
  const act = async (action: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await action(); setEditing(undefined); await onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : 'Unable to update locations'); }
    finally { setBusy(false); }
  };
  const renderNode = (location: Location): React.ReactNode => (
    <li key={location.id}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, flexWrap: 'wrap' }}>
        <span>{location.name}</span><small>{location.type}</small>
        <small>{location.asset_count || 0} assets · {location.sample_count || 0} lab samples</small>
        <button className="btn btn-secondary btn-sm" disabled={busy} aria-label={`Rename ${location.name}`} onClick={() => { setEditing(location.id); setName(location.name); }}>Rename</button>
        <button className="btn btn-secondary btn-sm" disabled={busy} aria-label={`Delete ${location.name}`} onClick={async () => {
          if (await confirm(`Delete ${location.name}? Referenced locations cannot be deleted.`)) void act(() => locationsService.deleteLocation(location.id));
        }}>Delete</button>
        {editing === location.id && <form onSubmit={event => { event.preventDefault(); if (name.trim()) void act(() => locationsService.updateLocation(location.id, { name: name.trim() })); }}>
          <label>New name<input value={name} onChange={event => setName(event.target.value)} required /></label>
          <button className="btn btn-secondary btn-sm" disabled={busy}>Save name</button><button className="btn btn-secondary btn-sm" type="button" onClick={() => setEditing(undefined)}>Cancel</button>
        </form>}
      </div>
      {locations.some(child => child.parent_location_id === location.id) && <ul style={{paddingLeft: 24}}>
        {locations.filter(child => child.parent_location_id === location.id).map(renderNode)}
      </ul>}
    </li>
  );
  return <section className="locations-content">
    {dialog}<h3>Locations</h3>
    {error && <p role="alert">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {unconverted.length > 0 && <div className="info-banner">
      This facility has {unconverted.length} older location entries stored as assets.{' '}
      <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void act(async () => {
        const result = await locationsService.convertLocationAssets(facilityId);
        setNotice(`${result.converted} locations converted. ${result.skipped.length ? `Retained assets with records: ${result.skipped.map(item => `${item.name} (${item.reason})`).join('; ')}` : 'All converted entries were removed from the old asset hierarchy.'}`);
      })}>Convert them to locations</button>
    </div>}
    <CreateLocationInline facilityId={facilityId} locations={locations} onCreated={() => { void onChanged(); }} />
    {locations.length === 0 ? <p>No locations yet. Add a building, wing, floor, zone or room.</p> : <ul>
      {locations.filter(location => !location.parent_location_id || !locations.some(parent => parent.id === location.parent_location_id)).map(renderNode)}
    </ul>}
  </section>;
};
export default FacilityLocations;
